import { useStyles } from '../styles'
import React, { useState } from 'react'
import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@material-ui/core'
import MoreHoriz from 'components/material-symbol/icons/MoreHoriz'
import DeleteOutlinedIcon from 'components/material-symbol/icons/Delete'
import EmailOutlinedIcon from 'components/material-symbol/icons/Email'
import { DateTime } from 'luxon'
import { useIsTablet } from 'lib/hooks'
import { useMutation } from '@apollo/client'
import { REVOKE_GUARDIAN_INVITATION } from '../queries'
import { useSnackbar, useSnackbarError, useSnackbarSuccess } from 'lib/snackbar'

const InvitedGuardianItem = ({ invitedGuardian, refetch }) => {
  const classes = useStyles()
  const isMobileOrTablet = useIsTablet()
  const [anchorEl, setAnchorEl] = useState(null)
  const [revokeModalOpen, setRevokeModalOpen] = useState(false)
  const { enqueueSnackbar } = useSnackbar()
  const snackbarError = useSnackbarError(enqueueSnackbar)
  const snackbarSuccess = useSnackbarSuccess(enqueueSnackbar)

  const { id, expiresAt, inviteName, inviteEmail, status } = invitedGuardian

  const [revokeGuardianInvitation] = useMutation(REVOKE_GUARDIAN_INVITATION)

  const getStatusText = status => {
    if (isMobileOrTablet) {
      return status === 'PENDING'
        ? 'Pending'
        : status === 'EXPIRED'
          ? 'Expired'
          : ''
    }
    return status === 'PENDING'
      ? 'Pending Associated Junior Players'
      : status === 'EXPIRED'
        ? 'Expired Junior Players'
        : ''
  }

  const handleMoreClick = event => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }
  const handleClose = event => {
    event.stopPropagation()
    setAnchorEl(null)
  }

  const handleRevokeClick = event => {
    handleClose(event)
    setRevokeModalOpen(true)
  }

  const handleRevokeModalClose = () => {
    setRevokeModalOpen(false)
  }

  const handleRevokeInvite = async () => {
    try {
      const response = await revokeGuardianInvitation({
        variables: { invitationId: id },
      })
      if (response?.data?.revokeJrlGuardianInvitation?.success) {
        snackbarSuccess('Guardian invitation revoked successfully')
      } else {
        snackbarError('Failed to revoke invitation')
      }
    } catch (error) {
      snackbarError(error?.message || 'Failed to revoke invitation')
    }
    setRevokeModalOpen(false)
    refetch()
  }

  const AvatarWithNameAndExpiredDate = ({ inviteName, expiresAt }) => {
    const formattedExpiryDate = `Expires at ${DateTime.fromISO(
      expiresAt,
    ).toLocaleString(DateTime.DATE_MED)}`
    return (
      <Grid item container direction="row" alignItems="center" spacing={2}>
        <Grid item>
          <Avatar alt={inviteName} />
        </Grid>
        <Grid item>
          <ListItemText primary={inviteName} secondary={formattedExpiryDate} />
        </Grid>
      </Grid>
    )
  }

  return (
    <>
      <Grid container item spacing={1} className={classes.invitedGuardianItem}>
        <Grid lg={3} xs={5} item>
          <AvatarWithNameAndExpiredDate
            inviteName={inviteName}
            expiresAt={expiresAt}
          />
        </Grid>
        <Grid lg={4} className={classes.hideOnMobile} item>
          <ListItemText
            primary={inviteEmail}
            style={{ overflowWrap: 'anywhere' }}
          />
        </Grid>
        <Grid lg={4} xs={5} item className={classes.center}>
          <ListItemText primary={getStatusText(status)} />
        </Grid>
        <Grid lg={1} xs={2} item>
          <Button
            aria-controls={`simple-menu-${id}`}
            aria-haspopup="true"
            data-testid="menu-button"
            onClick={handleMoreClick}
          >
            <MoreHoriz fontSize="small" />
          </Button>
          <Menu
            id={`simple-menu-${id}`}
            keepMounted
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            getContentAnchorEl={null}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
          >
            <MenuItem component="a" href={`mailto:${inviteEmail}`}>
              <ListItemIcon>
                <EmailOutlinedIcon className={classes.icon} />
              </ListItemIcon>
              <ListItemText primary={inviteEmail} />
            </MenuItem>
            <MenuItem onClick={handleRevokeClick}>
              <ListItemIcon>
                <DeleteOutlinedIcon className={classes.icon} />
              </ListItemIcon>
              <ListItemText primary="Revoke Invitation" />
            </MenuItem>
          </Menu>
        </Grid>
      </Grid>
      <Dialog open={revokeModalOpen} onClose={handleRevokeModalClose}>
        <DialogTitle>Revoke Invitation</DialogTitle>
        <DialogContent>
          <Typography>
            Revoke the invitation for this person to manage pending junior
            player accounts.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRevokeModalClose}>Cancel</Button>
          <Button
            onClick={() => handleRevokeInvite()}
            variant="contained"
            style={{
              backgroundColor: '#d32f2f',
              color: '#fff',
            }}
          >
            Revoke Invite
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default InvitedGuardianItem
